import { FC } from 'react'
import { useTranslate } from 'react-admin'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import IconButton from '@mui/material/IconButton'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import AddIcon from '@mui/icons-material/Add'
import { TransformNodeData, WorkflowNodeData } from '../types'
import { themeColor } from '../../../layout/theme/color'
import ExpressionTextField from '../autocomplete/ExpressionTextField'

interface TransformConfigProps {
  data: TransformNodeData
  onUpdate: (data: Partial<WorkflowNodeData>) => void
}

const TransformConfig: FC<TransformConfigProps> = ({ data, onUpdate }) => {
  const translate = useTranslate()

  const addMapping = () => {
    onUpdate({ mappings: [...data.mappings, { from: '', to: '' }] })
  }

  const removeMapping = (index: number) => {
    onUpdate({ mappings: data.mappings.filter((_, i) => i !== index) })
  }

  const updateMapping = (index: number, field: 'from' | 'to', value: string) => {
    const updated = data.mappings.map((m, i) => (i === index ? { ...m, [field]: value } : m))
    onUpdate({ mappings: updated })
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <TextField
        label={translate('workflow.config.label')}
        value={data.label}
        onChange={(e) => onUpdate({ label: e.target.value })}
        size="small"
        fullWidth
      />

      <Box>
        <Typography variant="caption" sx={{ fontWeight: 600, color: themeColor.slate60, mb: 1, display: 'block' }}>
          {translate('workflow.config.fieldMapping')}
        </Typography>
        {data.mappings.map((mapping, index) => (
          <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
            <ExpressionTextField
              size="small"
              placeholder="source.field"
              value={mapping.from}
              onChange={(e) => updateMapping(index, 'from', e.target.value)}
              sx={{ flex: 1 }}
            />
            <Typography variant="caption" sx={{ color: themeColor.slate40 }}>→</Typography>
            <TextField
              size="small"
              placeholder="target.field"
              value={mapping.to}
              onChange={(e) => updateMapping(index, 'to', e.target.value)}
              sx={{ flex: 1 }}
            />
            <IconButton size="small" onClick={() => removeMapping(index)}>
              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        ))}
        <Button size="small" startIcon={<AddIcon />} onClick={addMapping} sx={{ textTransform: 'none' }}>
          Add mapping
        </Button>
      </Box>
    </Box>
  )
}

export default TransformConfig
